/**
 * Company: SPHMedia
 * Description: This is the base page component for all BH pages
 */

import CASBlock from "@components/CASBlock/CASBlock";
import { MainContainerNew } from "@components/Container/MainContainerNew";
import CatfishAd from "@components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import PrestitialAd from "@components/Elements/Advertisement/PrestitialAd";
import { FooterNew } from "@components/FooterNew/FooterNew";
import { HeaderNew } from "@components/HeaderNew/HeaderNew";
import { pubPageAdTargeting } from "@config/ads";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import type { GADataProps } from "@elements/GAData/GAData";
import GAData from "@elements/GAData/GAData";
import type { MetaTagsProps } from "@elements/MetaTags/MetaTags";
import MetaTags from "@elements/MetaTags/MetaTags";
import { ARTICLE_REDESIGN, CAS_EI_MODEL_ID } from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import useOKTAUserInfo from "@hooks/useOktaUserInfo";
import useScript from "@hooks/useScript/useScript";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { ReactElement } from "react";

export type AdsSettings = {
  isPrestitialEnabled: boolean | undefined;
  isCatfishEnabled: boolean | undefined;
  topOverlayImpressions: number;
  topOverlayValidity: number;
  isOutbrainEnabled: boolean | undefined;
};

export type BHArticleBasePageProps = {
  adsUniqueSectionName?: string;
  children?: (adsSettings: AdsSettings) => React.ReactNode;
  hasLb1Ads?: boolean;
  gaData?: GADataProps;
  metaTags?: MetaTagsProps;
  pageAdTargetValue?: PageAdTargetValue;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  prestitialPageTargeting?: PageAdTargetValue;
};

/**
 * BHBasePage
 * Description: The base page component for all BH pages
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function BHArticleBasePage({
  adsUniqueSectionName,
  children,
  hasLb1Ads = true,
  gaData,
  metaTags,
  pageAdTargetValue,
  gsChannels,
  articleSlotTarget,
  prestitialPageTargeting,
}: BHArticleBasePageProps): ReactElement {
  useOKTAUserInfo();
  useScript({
    id: "mysph-lightbox-lib",
    src: import.meta.env.VITE_SPH_LIGHTBOX as string,
  });
  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  const adsSettings: AdsSettings = useGlobalAdSetting();

  const pageAdTarget =
    pageAdTargetValue &&
    (pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
      string,
      PageAdTargetValue
    >);
  const prestitialPageAdTarget =
    prestitialPageTargeting &&
    (pubPageAdTargeting[prestitialPageTargeting] as AdTargetingType<
      string,
      PageAdTargetValue
    >);

  const lb1PageNumberSlotTarget: AdTargetingType<ArticleAdTargetKeys, string> =
    {
      key: ArticleAdTargetKeys.PAGE_NUMBER,
      value: "1",
    };

  return (
    <>
      {gaData && <GAData {...gaData} />}
      {metaTags && <MetaTags {...metaTags} />}
      <MainContainerNew>
        {parseInt(ARTICLE_REDESIGN) === 1 ? (
          <>
            <div className="flex justify-center w-full bg-white-200 border-b-[1px] border-black-900">
              {hasLb1Ads && (
                <AdsLb1
                  uniqueSectionName={adsUniqueSectionName}
                  {...(pageAdTargetValue && {
                    pageAdTargetType: pageAdTargetValue,
                  })}
                  hasStripeBg={false}
                  wrapperStyle="mb-0"
                  isArticlePage={true}
                  gsChannels={gsChannels}
                  articleSlotTarget={[
                    ...(articleSlotTarget ?? []),
                    lb1PageNumberSlotTarget,
                  ]}
                />
              )}
            </div>
            <HeaderNew hideScrollableSectionMenu={true} />
            <PrestitialAd
              slot={GoogleAdsSlotFactory.prestitial(adsUniqueSectionName)}
              isPrestitialEnabled={adsSettings.isPrestitialEnabled}
              {...(prestitialPageAdTarget && {
                pubAdTargeting: [prestitialPageAdTarget],
              })}
              topOverlayImpressions={adsSettings.topOverlayImpressions}
              topOverlayValidity={adsSettings.topOverlayValidity}
              gsChannels={gsChannels}
              articleSlotTarget={articleSlotTarget?.slice(0, 2)}
            />
            {typeof children === "function" ? children(adsSettings) : children}
          </>
        ) : (
          <>
            <HeaderNew />
            <div className="max-w-screen-cxl w-full px-sm mx-auto">
              {hasLb1Ads && (
                <AdsLb1
                  uniqueSectionName={adsUniqueSectionName}
                  {...(pageAdTargetValue && {
                    pageAdTargetType: pageAdTargetValue,
                  })}
                  isArticlePage={true}
                  gsChannels={gsChannels}
                  articleSlotTarget={articleSlotTarget}
                />
              )}
              <PrestitialAd
                slot={GoogleAdsSlotFactory.prestitial(adsUniqueSectionName)}
                isPrestitialEnabled={adsSettings.isPrestitialEnabled}
                {...(prestitialPageAdTarget && {
                  pubAdTargeting: [prestitialPageAdTarget],
                })}
                topOverlayImpressions={adsSettings.topOverlayImpressions}
                topOverlayValidity={adsSettings.topOverlayValidity}
                gsChannels={gsChannels}
                articleSlotTarget={articleSlotTarget?.slice(0, 2)}
              />
              {typeof children === "function"
                ? children(adsSettings)
                : children}
            </div>
          </>
        )}
      </MainContainerNew>
      <FooterNew />
      <CatfishAd
        slot={GoogleAdsSlotFactory.catfish(adsUniqueSectionName)}
        isCatfishEnabled={adsSettings.isCatfishEnabled}
        {...(pageAdTarget && { pubAdTargeting: [pageAdTarget] })}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
      />
      <CASBlock casId={CAS_EI_MODEL_ID} />
    </>
  );
}
